import { useCallback, useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid } from '@mui/material';
import _, { flowRight as compose } from 'lodash';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'providers';
import { useRecoilState } from 'recoil';

import AxlButton from 'AxlMUIComponent/AxlButton';
import AxlConfirmation from 'AxlMUIComponent/AxlConfirmation';
import AxlTable from 'AxlMUIComponent/AxlTable';
import AxlDialog from 'components/AxlDialog';
import VehicleDocument from 'components/VehicleDocument';
import acl from 'constants/acl';
import { getVehicles, vehicleRemove } from 'repositories';
import VehicleAddNew from '../VehicleAddNew';
import schema from './schema';
import { vehicleResultState, vehicleState } from './state';

function VehiclesListCompose(props) {
  const { type = 'DEFAULT', store } = props;

  const [query, setQuery] = useRecoilState(vehicleState);
  const [result, setResult] = useRecoilState(vehicleResultState);

  const [removing, setRemoving] = useState(null);
  const [deleteDialogItem, setDeleteDialogItem] = useState(null);
  const [editDialogItem, setEditDialogItem] = useState(null);
  const [documentDialogItem, setDocumentDialogItem] = useState(null);

  const { fields } = schema[type];

  const { permissionStore } = store;
  const isDeniedEdit = permissionStore.isDenied(acl.VEHICLES.EDIT);
  const isDeniedDelete = permissionStore.isDenied(acl.VEHICLES.DELETE);

  const getVehicleList = useCallback(() => {
    getVehicles(query).then((res) => {
      if (res.ok && res.status === 200) setResult({ items: res.data.results, size: 10, count: res.data.total_records });
    });
  }, [query, setResult]);

  useEffect(() => {
    getVehicleList();
  }, [editDialogItem, getVehicleList]);

  const removeVehicle = (id) => {
    setRemoving(id);
    vehicleRemove(id).then((res) => {
      if (res.ok && res.status === 204) {
        onChange(1);
        setDeleteDialogItem(null);
      }
      setRemoving(false);
    });
  };

  const objectRestructured = (obj) => {
    return _.merge(
      _.pick(obj, [
        'id',
        'color',
        'insurance',
        'registration',
        'vehicle_name',
        'license_plate',
        'license_state',
        'registration_record_issued_ts',
        'registration_record_expired_ts',
        'insurance_card_url',
        'registration_record_url',
        'insurance_card_issued_ts',
        'insurance_card_expired_ts',
      ]),
      _.pick(_.get(obj, 'car'), ['year', 'make', 'model', 'submodel', 'type']),
    );
  };

  const rerender = {
    vehicle_info: (value, item, result) =>
      `${_.get(item, 'car.year', '')} ${_.get(item, 'car.make', '')} ${_.get(item, 'car.model', '')} ${_.get(item, 'car.submodel', '')}`,
    vehicle_type: (value, item, result) => `${_.get(item, 'car.type', '')}`,
    action: (fieldName, item, result) => (
      <Grid container wrap={'nowrap'} justifyContent={'flex-end'}>
        {(item['insurance_card_url'] || item['registration_record_url']) && (
          <AxlButton icon spacing={0} padding={0} tooltip={{ title: 'documents' }} onClick={() => setDocumentDialogItem(item)}>
            <DescriptionIcon
              fontSize={'small'}
              style={item['insurance_card_expired'] || item['registration_record_expired'] ? { color: 'red' } : {}}
            />
          </AxlButton>
        )}
        {!isDeniedEdit && (
          <AxlButton icon spacing={0} padding={0} tooltip={{ title: 'edit' }} onClick={() => setEditDialogItem(item)}>
            <EditIcon fontSize={'small'} />
          </AxlButton>
        )}
        {!isDeniedDelete && (
          <AxlButton
            icon
            spacing={0}
            padding={0}
            tooltip={{ title: 'remove' }}
            disabled={removing === item.id}
            loading={removing === item.id}
            onClick={() => setDeleteDialogItem(item)}
          >
            <DeleteIcon fontSize={'small'} />
          </AxlButton>
        )}
      </Grid>
    ),
  };

  const onChange = (pageNumber) => {
    const _query = _.assign({}, query, { page_number: pageNumber - 1 });
    setQuery(_query);
  };

  return (
    <Box bgcolor={'#fff'}>
      {result && <AxlTable page={query.page_number + 1} result={result} fields={fields} rerender={rerender} onChange={onChange} />}

      {/* Document Dialog */}
      <AxlDialog
        isOpen={!!documentDialogItem}
        handleClose={() => setDocumentDialogItem(null)}
        childrenTitle={<Box fontWeight={700}>Vehicle Documents</Box>}
      >
        {documentDialogItem && <VehicleDocument item={documentDialogItem} />}
      </AxlDialog>

      {/* Edit Dialog */}
      <AxlDialog isOpen={!!editDialogItem} handleClose={() => setEditDialogItem(null)} childrenTitle={<Box fontWeight={700}>Edit Vehicle</Box>}>
        {editDialogItem && <VehicleAddNew onClose={() => setEditDialogItem(null)} data={objectRestructured(editDialogItem)} />}
      </AxlDialog>

      {/* Delete Confirmation Dialog */}
      <AxlDialog
        isOpen={!!deleteDialogItem}
        handleClose={() => setDeleteDialogItem(null)}
        childrenTitle={<Box fontWeight={700}>Delete Vehicle</Box>}
        maxWidth="xs"
      >
        {deleteDialogItem && (
          <AxlConfirmation
            title={`Are you sure to delete vehicle [${_.get(deleteDialogItem, 'vehicle_name', 'N/A Name')}] ${_.get(deleteDialogItem, 'car.year', '')} ${_.get(deleteDialogItem, 'car.make', '')} ${_.get(deleteDialogItem, 'car.model', '')} ${_.get(deleteDialogItem, 'car.submodel', '')}?`}
            doText={`Delete`}
            loading={removing}
            disabled={removing}
            onClose={() => setDeleteDialogItem(null)}
            onDo={() => removeVehicle(deleteDialogItem.id)}
            doProps={{ color: 'error' }}
          />
        )}
      </AxlDialog>
    </Box>
  );
}

export default compose(withRouter, inject('store'), observer)(VehiclesListCompose);
